import { Component, inject, OnDestroy, OnInit, SecurityContext } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { Auditor } from "@app/Domain/Models/auditor.model";
import { Survey } from "@app/Domain/Models/survey.model";
import { SurveyAuditor } from "@app/Domain/Models/surveyAuditor.model";

import { AuditorService } from "@core/services/auditor.service";
import { HttpLanguageService } from "@core/services/language/http-language.service";
import { SurveyService } from "@core/services/survey.nps.service";

import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { Dialog, DialogModule } from "primeng/dialog";

import { TranslocoPipe } from "@jsverse/transloco";
import { ModalService } from "@services/modal.service";
import { NotificationService } from "@services/notification.service";
import { SessionStorageService } from "@services/session-storage.service";
import { SurveyAuditorService } from "@services/survey.auditor.service";
import { forkJoin } from "rxjs";

@Component({
    selector: "nps",
    imports: [TranslocoPipe, Dialog, DialogModule, ConfirmDialogModule, ButtonModule],
    templateUrl: "./nps.component.html",
    styleUrls: ["./nps.component.css"]
})
export class NpsComponent implements OnInit, OnDestroy {
    currentYear: number;
    auditor?: Auditor;

    survey: Survey;
    surveys: Survey[] = [];

    private auditorService = inject(AuditorService);
    private router = inject(Router);
    private modalService = inject(ModalService);
    private sessionStorage = inject(SessionStorageService);
    private service = inject(SurveyAuditorService);
    private notification = inject(NotificationService);
    private sanitizer = inject(DomSanitizer);
    private surveyService = inject(SurveyService);
    private httpLanguageService = inject(HttpLanguageService);

    private readonly TRUSTED_HOSTS: string[] = ["forms.office.com", "forms.microsoft.com"];
    private intervalId: ReturnType<typeof setInterval> | null = null;

    private isUrlTrusted(url: string): boolean {
        try {
            const parsed = new URL(url);
            if (parsed.protocol !== "https:") {
                return false;
            }
            return this.TRUSTED_HOSTS.some((host) => parsed.hostname === host || parsed.hostname.endsWith("." + host));
        } catch {
            return false;
        }
    }

    isUserAdmin: boolean = this.auditorService.isAdm();
    isUserAuditor: boolean = !this.auditorService.isAdm() && !this.auditorService.isCoordenador();
    isUserCoordenador: boolean = this.auditorService.isCoordenador();

    displayDialog: boolean = false;

    ngOnInit(): void {
        this.auditor = this.sessionStorage.get("auditor");

        if (!this.isUserAdmin && !this.isUserAuditor && !this.isUserCoordenador) {
            this.router.navigate(["/Home"]);
            return;
        }

        const now = new Date();
        this.currentYear = now.getFullYear();

        const baseRequests = {
            idiomas: this.httpLanguageService.getLanguages(),
            surveys: this.surveyService.getAll(),
        };

        forkJoin(baseRequests).subscribe({
            next: (result) => {
                this.surveys = result.surveys.map((survey: Survey) => ({
                    ...survey
                }));

                const surveyLanguage = result.idiomas.filter(
                    (language) => language.code == this.auditor.idiomaAuditor
                )[0];

                this.survey = result.surveys.filter(
                    (survey) => survey.year == this.currentYear && survey.language == surveyLanguage.code
                )[0];

                if (this.showSurveyModal()) {
                    setTimeout(() => {
                        this.openModalPeriodically();
                    }, 1000);
                }
            }
        });
    }

    ngOnDestroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    openModalPeriodically(): void {
        const checkHasCompletedSurvey = () => {
            if (!this.auditor?.hasCompletedSurvey) {
                this.showDialog();
            }
        };

        checkHasCompletedSurvey();

        this.intervalId = setInterval(checkHasCompletedSurvey, 30 * 60 * 1000);
    }

    showDialog(): void {
        this.displayDialog = true;
    }

    closeDialog(): void {
        this.displayDialog = false;
    }

    redirectToNPS(): void {
        const surveyUrl = this.survey.url;

        const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, surveyUrl);

        if (!sanitizedUrl) {
            this.notification.error("module.survey.urlNotSanitized");

            return;
        }

        if (!this.isUrlTrusted(sanitizedUrl)) {
            this.notification.error("module.survey.urlNotListedInTrustedHosts");

            return;
        }

        window.open(sanitizedUrl, "_blank", "noopener,noreferrer");

        const surveyAuditorPost: SurveyAuditor = {
            idSurveyAuditor: 0,
            codAuditor: this.auditor?.codAuditor,
            hasCompletedSurvey: true,
            idSurvey: this.survey.idSurvey
        };

        this.service.addSurveyAuditor(surveyAuditorPost).subscribe({
            next: (result) => {
                if (this.auditor) {
                    this.auditor.hasCompletedSurvey = result.hasCompletedSurvey;
                    this.sessionStorage.set("auditor", this.auditor);
                }
                this.closeDialog();
            },
            complete: () => {
                this.notification.success("module.npsSurvey.alreadyAnswered");
                setTimeout(() => {
                    window.location.reload();
                }, 5000); 
            }
        });
    }

    later(): void {
        this.modalService.close();
    }

    getDaysPassedSinceStartOfYear(): number {
        const today = new Date();
        const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
        const differenceInMilliseconds = today.getTime() - firstDayOfYear.getTime();
        const daysPassed = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
        return daysPassed;
    }

    enteredSecondSemestre(): boolean {
        const daysPassed = this.getDaysPassedSinceStartOfYear();

        return daysPassed >= 182;
    }

    currentYearHaveSurveyRegistered(): boolean {
        const filteredSurveys = this.surveys.filter((survey) => survey.year == this.currentYear);

        return filteredSurveys.length > 0;
    }

    haveThisCurrentYearSurveyAnswered(): boolean {
        const now = new Date();
        this.currentYear = now.getFullYear();

        return this.auditor?.hasCompletedSurvey;
    }

    showSurveyModal(): boolean {
        if (!this.enteredSecondSemestre()) return false;

        if (!this.currentYearHaveSurveyRegistered()) return false;

        if (this.haveThisCurrentYearSurveyAnswered()) return false;

        return true;
    }
}
